import { isArray, isEmpty } from 'lodash';
import { graphql } from 'react-apollo';

import SalesforceUser from '~/components/Salesforce/User';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '~/components/Table';

import Link from './Link';
import query from './query.graphql';
import styles from './TraitsTable.style';

const SFDC_USER_ID_FIELDS = [
  'Owner ID',
  'Created By ID',
  'Last Modified By ID',
];

class TraitsTable extends React.Component {
  renderKey = (key, value, salesforceUsers) => {
    if (salesforceUsers && SFDC_USER_ID_FIELDS.some((field) => field === key)) {
      const salesforceUser = salesforceUsers.find((user) => user.id === value);
      return <SalesforceUser salesforceUser={salesforceUser} />;
    }

    return this.renderValue(value);
  };

  renderValue = (value) => {
    const { classes } = this.props;

    if (!value) {
      return String.fromCharCode(8212);
    } else if (isArray(value)) {
      return (
        <div className={classes.pills}>
          {value.map((s) =>
            typeof s === 'string' ? (
              <div className={classes.pill} key={s}>
                {this.renderValue(s)}
              </div>
            ) : (
              this.renderValue(s)
            ),
          )}
        </div>
      );
    } else if (typeof value === 'string') {
      const isAvatar =
        value.match('pbs.twimg.com') || value.match('secure.gravatar.com');
      if (isAvatar) {
        return <img height={48} src={value} alt="Avatar" />;
      } else {
        return <Link str={value} />;
      }
    } else if (typeof value === 'object' && value.url) {
      return (
        <img height={48} key={value.url} alt="thumbnail" src={value.url} />
      );
    } else if (typeof value === 'object' && value.title && value.value) {
      return (
        <span>
          {value.title}:{' '}
          <a target="blank" rel="noopener noreferrer" href={value.value}>
            {value.value}
          </a>
        </span>
      );
    } else if (typeof value === 'object') {
      return Object.keys(value).map((val) => (
        <div className={classes.pill} key={val}>
          {val}: {this.renderValue(value[val])}
        </div>
      ));
    } else if (value && value.toString) {
      return value.toString();
    } else {
      return String.fromCharCode(8212);
    }
  };

  renderRows = () => {
    const {
      classes,
      data: { loading, error, salesforceUsers },
      traits,
    } = this.props;

    if (error || loading) {
      return null;
    }

    return Object.entries(traits).map(([key, value]) => (
      <TableRow key={key}>
        <TableCell className={classes.tableKey} slim sub>
          {key}
        </TableCell>
        <TableCell className={classes.tableValue}>
          {this.renderKey(key, value, loading ? [] : salesforceUsers)}
        </TableCell>
      </TableRow>
    ));
  };

  render() {
    const { classes, sectionTitle, sectionAdornment, traits } = this.props;

    if (isEmpty(traits)) return null;

    return (
      <Table sidebar className={classes.detailsTable}>
        <TableHead>
          <TableRow>
            <TableCell
              leftAligned
              className={classes.tableTitle}
              innerClassName={classes.tableTitleInner}>
              {sectionTitle}
            </TableCell>
            <TableCell
              rightAligned
              className={classes.tableTitle}
              innerClassName={classes.tableTitleInner}>
              {sectionAdornment}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{this.renderRows()}</TableBody>
      </Table>
    );
  }
}

TraitsTable.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.shape({
    error: PropTypes.object,
    loading: PropTypes.boolean,
  }).isRequired,
  traits: PropTypes.object,
  sectionTitle: PropTypes.node,
  sectionAdornment: PropTypes.node,
};

function salesforceUserIds(traits) {
  return Object.entries(traits).reduce((ids, [key, value]) => {
    if (SFDC_USER_ID_FIELDS.some((field) => field === key)) {
      return [...ids, value];
    }
    return ids;
  }, []);
}

const StyledTraitsTable = withStyles(styles)(TraitsTable);

const WrappedTraitsTable = graphql(query, {
  options: (props) => ({
    variables: { externalIds: salesforceUserIds(props.traits) },
  }),
})(StyledTraitsTable);

export default WrappedTraitsTable;
export { StyledTraitsTable as TraitsTable };
